import { Button, Dropdown, Tag, Typography } from 'antd'
import type { TableColumnsType } from 'antd'
import { createStyles } from 'antd-style'
import {
  ChevronRight,
  Eye,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2
} from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ProTableContainer,
  TableActionColumnTitle,
  UserCell
} from 'src/components'

import type { OrgTypeFilter, OrgUnit } from '../types'
import {
  KIND_COLOR,
  apiTypeToKind,
  buildChildCountMap,
  buildOrgTree,
  childKindOf,
  collectOrgTreeIds,
  pruneOrgTree,
  tintHex,
  type OrgUnitTreeNode
} from '../utils'

import { OrgTypeIcon } from './OrgTypeIcon'

const { Link, Text } = Typography

const useTreeStyles = createStyles(({ token }) => ({
  table: {
    '.ant-table-tbody > tr > td': {
      padding: '10px 12px',
      borderBottom: `1px solid ${token.colorBorderSecondary}`,
      verticalAlign: 'middle'
    },
    // Tree (name) column: lay out antd's prepended indent spacer + the custom
    // expand icon + the cell content as one centered flex row. antd v6 prepends
    // a `float:left`, `height:1px` indent span ahead of the (non-floated) custom
    // expand icon; because the 1px float only intrudes on the first line and the
    // content span was `width:100%`, the content wrapped to a second line — the
    // chevron was orphaned above it and child rows lost their indentation.
    // Flexing the cell turns the indent span's `padding-left` into real layout
    // width (so depth indents correctly) and keeps the icon + content inline.
    '.ant-table-tbody > tr.ant-table-row > td:first-child': {
      display: 'flex',
      alignItems: 'center'
    },
    '.ant-table-row-indent': {
      float: 'none'
    },
    '.ant-table-tbody > tr:hover > td': {
      background: `${token.colorFillTertiary} !important`
    }
  },
  cell: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    flex: '1 1 auto'
  },
  typeIcon: {
    width: 22,
    height: 22,
    borderRadius: 5,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto'
  },
  text: {
    display: 'inline-flex',
    flexDirection: 'column',
    minWidth: 0,
    lineHeight: 1.3,
    flex: 1
  },
  nameRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0
  },
  nameLink: {
    fontSize: 13
  },
  typeTag: {
    marginInlineEnd: 0,
    fontSize: 11,
    border: 'none',
    flex: '0 0 auto'
  },
  description: {
    fontSize: 11,
    color: token.colorTextSecondary
  },
  expandSlot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    marginInlineEnd: 8,
    verticalAlign: 'middle',
    flex: '0 0 auto'
  },
  expandButton: {
    padding: 0,
    cursor: 'pointer',
    borderRadius: 4,
    border: 'none',
    background: 'transparent',
    color: token.colorTextTertiary,
    transition: `background-color ${token.motionDurationMid}, color ${token.motionDurationMid}`,

    '&:hover': {
      background: token.colorFillTertiary,
      color: token.colorText
    },

    '&:focus-visible': {
      outline: `2px solid ${token.colorPrimaryBorder}`,
      outlineOffset: 1
    }
  },
  expandChevron: {
    transition: `transform ${token.motionDurationMid} ease`,
    display: 'block'
  },
  headcount: {
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums'
  },
  muted: {
    fontSize: 12,
    color: token.colorTextSecondary
  }
}))

type Props = {
  units: OrgUnit[]
  search: string
  typeFilter: OrgTypeFilter
  onSelect: (unit: OrgUnit) => void
  /** Omitted when the viewer may not change the structure. */
  onEdit?: (unit: OrgUnit) => void
  onAddChild?: (unit: OrgUnit) => void
  onDelete?: (unit: OrgUnit) => void
}

export const OrgTreeView = ({
  units,
  search,
  typeFilter,
  onSelect,
  onEdit,
  onAddChild,
  onDelete
}: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useTreeStyles()

  const q = search.trim().toLowerCase()
  const isFiltered = q.length > 0 || typeFilter !== 'all'

  // Build once per data change; prune separately when the filter changes.
  const fullTree = useMemo(() => buildOrgTree(units), [units])

  const treeData = useMemo(() => {
    if (!isFiltered) return fullTree
    const matches = (n: OrgUnitTreeNode) => {
      if (typeFilter !== 'all' && apiTypeToKind(n.type) !== typeFilter)
        return false
      if (!q) return true
      return n.name.toLowerCase().includes(q)
    }

    return pruneOrgTree(fullTree, matches)
  }, [fullTree, q, typeFilter, isFiltered])

  // Real (unfiltered) direct-children count keyed by id, so the sub-units
  // column doesn't drop when search hides some children.
  const childCountById = useMemo(() => buildChildCountMap(units), [units])

  // Initial expansion: divisions + departments open, teams collapsed. When a
  // filter is active, every visible node is expanded so matches at any depth
  // are reachable. The `key` prop on the table re-mounts on filter toggle so
  // these `defaultExpandedRowKeys` are re-applied without controlled state.
  const defaultExpandedRowKeys = useMemo(() => {
    if (isFiltered) return collectOrgTreeIds(treeData)
    return units
      .filter((u) => apiTypeToKind(u.type) !== 'team')
      .map((u) => u.id)
  }, [units, treeData, isFiltered])

  const columns: TableColumnsType<OrgUnitTreeNode> = [
    {
      title: t('organization.tree.columns.name'),
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      render: (_, row) => {
        const kind = apiTypeToKind(row.type)
        const color = KIND_COLOR[kind]
        const tint = tintHex(color)

        // The cell flexes (see `table` styles): this span is the grow item
        // beside the indent spacer and expand icon, holding the type icon and
        // the name/description text block.
        return (
          <span className={styles.cell}>
            <span
              className={styles.typeIcon}
              style={{ background: tint, color }}
            >
              <OrgTypeIcon kind={kind} size={12} />
            </span>
            <span className={styles.text}>
              <span className={styles.nameRow}>
                <Link
                  strong
                  className={styles.nameLink}
                  onClick={() => onSelect(row)}
                  ellipsis
                >
                  {row.name}
                </Link>
                <Tag
                  className={styles.typeTag}
                  style={{ background: tint, color }}
                >
                  {t(`organization.types.${kind}`)}
                </Tag>
              </span>
              {row.description ? (
                <Text
                  type="secondary"
                  className={styles.description}
                  ellipsis={{ tooltip: row.description }}
                >
                  {row.description}
                </Text>
              ) : null}
            </span>
          </span>
        )
      }
    },
    {
      title: t('organization.tree.columns.lead'),
      dataIndex: 'head_user_detail',
      key: 'lead',
      width: 240,
      render: (_, row) => {
        const head = row.head_user_detail
        if (!head) {
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('organization.tree.leadVacant')}
            </Text>
          )
        }
        return (
          <UserCell
            firstName={head.first_name}
            lastName={head.last_name}
            email={head.email}
            color={KIND_COLOR[apiTypeToKind(row.type)]}
            size="sm"
            hideMeta
          />
        )
      }
    },
    {
      title: t('organization.tree.columns.headcount'),
      dataIndex: 'users_count',
      key: 'headcount',
      width: 110,
      align: 'right' as const,
      render: (v: number) => (
        <Text strong className={styles.headcount}>
          {v}
        </Text>
      )
    },
    {
      title: t('organization.tree.columns.subUnits'),
      key: 'subUnits',
      width: 110,
      align: 'right' as const,
      render: (_, row) => {
        const n = childCountById[row.id] ?? 0
        if (n === 0) {
          return (
            <Text type="secondary" className={styles.muted}>
              —
            </Text>
          )
        }
        return (
          <Text type="secondary" className={styles.muted}>
            {t('organization.tree.subUnitsCount', { count: n })}
          </Text>
        )
      }
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 56,
      align: 'center' as const,
      render: (_, row) => {
        const kind = apiTypeToKind(row.type)
        const childKind = childKindOf(kind)

        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                {
                  key: 'view',
                  icon: <Eye size={14} />,
                  label: t('organization.actions.viewDetails'),
                  onClick: () => onSelect(row)
                },
                ...(onEdit
                  ? [
                      {
                        key: 'edit',
                        icon: <Pencil size={14} />,
                        label: t('organization.actions.edit'),
                        onClick: () => onEdit(row)
                      }
                    ]
                  : []),
                ...(childKind && onAddChild
                  ? [
                      {
                        key: 'addChild',
                        icon: <Plus size={14} />,
                        label: t(`organization.actions.add.${childKind}`),
                        onClick: () => onAddChild(row)
                      }
                    ]
                  : []),
                ...(onDelete
                  ? [
                      { type: 'divider' as const },
                      {
                        key: 'delete',
                        icon: <Trash2 size={14} />,
                        label: t('organization.actions.delete'),
                        danger: true,
                        onClick: () => onDelete(row)
                      }
                    ]
                  : [])
              ]
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreHorizontal size={14} />}
              onClick={(e) => e.stopPropagation()}
              aria-label={t('organization.actions.more')}
            />
          </Dropdown>
        )
      }
    }
  ]

  return (
    <ProTableContainer<OrgUnitTreeNode>
      options={false}
      showSearch={false}
      key={isFiltered ? 'filtered' : 'unfiltered'}
      className={styles.table}
      rowKey="id"
      dataSource={treeData}
      columns={columns}
      pagination={false}
      expandable={{
        defaultExpandedRowKeys,
        indentSize: 24,
        // Custom expand icon — a 22×22 inline-flex slot that matches the
        // type-icon footprint. Leaves get a transparent spacer of the same
        // size so their type icons line up with parents'. A single
        // ChevronRight rotates 90° when expanded.
        expandIcon: ({ expanded, onExpand, record, expandable }) => {
          if (!expandable) {
            return <span aria-hidden className={styles.expandSlot} />
          }

          return (
            <button
              type="button"
              aria-label={
                expanded
                  ? t('organization.tree.collapse')
                  : t('organization.tree.expand')
              }
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation()
                onExpand(record, e)
              }}
              className={cx(styles.expandSlot, styles.expandButton)}
            >
              <ChevronRight
                size={14}
                strokeWidth={2.25}
                className={styles.expandChevron}
                style={{
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)'
                }}
              />
            </button>
          )
        }
      }}
      locale={{
        emptyText: t('organization.tree.empty')
      }}
    />
  )
}
